// apps/web/src/pages/author/sections/Modes.tsx
//
// Phase 8 Plan 08 Task 3 — Modes form section (authr-01 / authr-03).
//
// recipe.modes: ModeDefinition[]   each: { id: string; name: string; charter?: string }
// schema: packages/shared/src/schemas/mode.ts (id is kebab-case, max 50;
// name is 1-100; charter is optional 1-500).
//
// authr-03 + authr-06 hygiene preserved; no visibleWhen here (Phase 8
// only emits visibleWhen UI on OptionCategory blocks per CONTEXT D-23).

import type { RecipeDefinitionV1 } from "@the-recipe-spread/shared";
import { useFieldArray, useFormContext } from "react-hook-form";
import { EmptyState } from "@/components/ui/empty-state";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FieldArrayRow } from "../FieldArrayRow";

const NEW_MODE_DEFAULTS = { id: "", name: "" };

export function Modes(): React.ReactElement {
  const { control } = useFormContext<RecipeDefinitionV1>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "modes",
  });

  return (
    <Fieldset legend="MODES">
      {fields.length === 0 ? (
        <EmptyState
          title="No modes yet."
          cta={{
            label: "Add mode",
            onClick: () => append(NEW_MODE_DEFAULTS),
          }}
        />
      ) : (
        <>
          {fields.map((field, index) => (
            <FieldArrayRow
              key={field.id}
              singular="mode"
              index={index}
              canMoveUp={index > 0}
              canMoveDown={index < fields.length - 1}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
              onRemove={() => remove(index)}
              deleteDialogTitle="Delete mode?"
              deleteDialogBody="This mode will be removed from the recipe."
              deleteConfirmLabel="Delete mode"
            >
              <FormField
                control={control}
                name={`modes.${index}.id`}
                render={({ field }) => (
                  <FormItem data-field-path={`modes.${index}.id`}>
                    <FormLabel>Id (kebab-case)</FormLabel>
                    <FormControl>
                      <Input {...field} autoFocus={!field.value} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`modes.${index}.name`}
                render={({ field }) => (
                  <FormItem data-field-path={`modes.${index}.name`}>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`modes.${index}.charter`}
                render={({ field }) => (
                  <FormItem data-field-path={`modes.${index}.charter`}>
                    <FormLabel>Charter (optional)</FormLabel>
                    <FormControl>
                      <Textarea {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FieldArrayRow>
          ))}
          <button
            type="button"
            onClick={() => append(NEW_MODE_DEFAULTS)}
            className="self-start text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
          >
            Add mode
          </button>
        </>
      )}
    </Fieldset>
  );
}
Modes.displayName = "Modes";
